import type { TwilioPhoneType } from '@/api/interfaces/twilio';
import type { ClientType, UserType } from '@/components/Views/Company/File/file.types';

type UnixTimestamp = `${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`;

export interface Conversation {
    id: number | null;
    branch: ConversationBranch;
    created_at: UnixTimestamp;
    clients: ConversationClient[];
    files: Array<{
        id: number;
        ref_number: string;
        address: string;
    }>;
    is_notification_recipient: boolean;
    elapsed_time: string; // "Now", "5 mins ago"
    message_preview: string | null;
    num_unread: number;
}

export interface ConversationBranch {
    name: string;
    phone_number: string;
    title_company: string | null;
}

export interface ConversationClient {
    email: string;
    id: number;
    phone_number: string | null;
    first_name: string | null;
    last_name: string | null;
}

export interface ConversationRecipient {
    name: string;
    role: UserType;
}

export interface FileDetailClient extends ConversationClient {
    role: ClientType;
    phone_type: TwilioPhoneType | null;
}

export const enum DateSeparatedMessageType {
    MESSAGE = 'message',
    DATE_SEPARATOR = 'date-separator',
}

export type DateSeparatedMessage =
    | { type: DateSeparatedMessageType.MESSAGE; sms: SmsMessage }
    | { type: DateSeparatedMessageType.DATE_SEPARATOR; date: string };

export const enum SmsDirection {
    RECEIVED = 'received',
    SENT = 'sent',
}

export const enum SmsOutboundStatus {
    PENDING = 'pending',
    SENT = 'sent',
    DELIVERED = 'delivered',
    UNDELIVERED = 'undelivered',
    READ = 'read',
    FAILED = 'failed',
}

export type MessageMediaStatus =
    | 'pending'
    | 'downloaded_from_twilio'
    | 'uploaded_to_storage'
    | 'scanned'
    | 'quarantine'
    | 'failed_download'
    | 'failed_upload'
    | 'failed_storage_location_update'
    | 'failed_unsupported_attachment';

export interface MessageMedia {
    content_type: string | null;
    filename: string | null;
    presigned_url: string | null;
    s3_object_key: string | null;
    size: string | null;
    status: MessageMediaStatus | null;
}

export interface SmsMessage {
    phone_number: string | null;
    created_at: string;
    direction: SmsDirection.RECEIVED | SmsDirection.SENT;
    outbound_status: SmsOutboundStatus | null;
    delivery_date: string | null;
    first_name: string | null;
    id: number;
    last_name: string | null;
    message: string | null;
    user_id: number | null;
    media: MessageMedia[];
}

/**
 * @deprecated Vue 3.5 has trouble building real runtime type definitions
 * from disciminated unions, so both Message types have been combined in
 * {@link SmsMessage}. This vestigial type is included for dev reference.
 */
interface _ReceivedSmsMessage extends SmsMessage {
    phone_number: string;
    direction: SmsDirection.RECEIVED;
    user_id: null;
}

/**
 * @deprecated Vue 3.5 has trouble building real runtime type definitions
 * from disciminated unions, so both Message types have been combined in
 * {@link SmsMessage}. This vestigial type is included for dev reference.
 */
interface _SentSmsMessage extends SmsMessage {
    phone_number: null;
    direction: SmsDirection.SENT;
    user_id: number;
}

export interface ConversationFile {
    id: number;
    branch_name: string;
    ref_number: string;
    address: string;
    close_date: string | null;
    status: 'Pre-close' | 'Scheduled';
    clients: FileDetailClient[];
    notification_recipients: ConversationRecipient[];
}

export interface ConversationDetails {
    files: ConversationFile[];
    notification_recipients: ConversationRecipient[];
}

export const enum SelectedSearchResultType {
    CLIENT = 'client',
    FILE = 'file',
}

// #region Response types
export interface UnreadSmsCount {
    unread_count: number;
}

type BaseConversationClientSearchResult = ConversationClient & { phone_type: TwilioPhoneType };

export type ConversationClientSearchResult = BaseConversationClientSearchResult & {
    files: BaseConversationFileSearchResult[];
};

type BaseConversationFileSearchResult = Omit<ConversationFile, 'status'>;

export type ConversationFileSearchResult = BaseConversationFileSearchResult & {
    clients: BaseConversationClientSearchResult[];
};

export interface GetConversationSearchResults {
    clients: ConversationClientSearchResult[];
    files: ConversationFileSearchResult[];
}

export interface PostMessagePayload {
    client_ids: number[];
    message: string;
    messaging_phone: string;
}
// #endregion
